import { useMutation, useQuery } from "@apollo/client";
import { Alert, App } from "antd";
import { useNavigate, useResolvedPath } from "react-router-dom";
import {
  ApplicantUser,
  ApplicantIdVerificationPreviewContainerApplicantUserDocument,
  ApplicantIdVerificationPreviewContainerIdentityVerificationCaseV1Document,
  ApplicantIdVerificationPreviewContainerIdentityVerificationCaseV1UpdateDocument,
  ApplicantIdVerificationPreviewContainerIdentityVerificationCaseV1DeleteDocument,
  IdentityVerificationCaseV1CaseDetailsApplication,
} from "../../../../generated";
import { lookupCountryQuery } from "./id-verification-country-query";
import { Country } from "../../../../types/country";
import { ComponentQueryLoader } from "../../../shared/component-query-loader";
import { AzureBlobResponse } from "../../../shared/file-upload/azure-upload";
import { useProfileSubmissionCheck } from "../../../shared/use-profile-submission-check";
import { IdVerificationPreview } from "./id-verification-preview";
import { getItem, setItem } from "../../../shared/local-storage";

interface IdVerificationPreviewContainerProps {
  applicantUserId: string;
}

export const IdVerificationPreviewContainer: React.FC<IdVerificationPreviewContainerProps> = (props) => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const caseId = getItem('caseId');
  const resolvedPath = useResolvedPath("..").pathname;
  const {
    data: applicantUserData,
    loading: applicantUserLoading,
    error: applicantUserError,
  } = useQuery(ApplicantIdVerificationPreviewContainerApplicantUserDocument, {
    variables: {
      applicantUserId: props.applicantUserId,
    },
  });
  const {
    data: identityVerificationCaseV1Data,
    loading: identityVerificationCaseV1Loading,
    error: identityVerificationCaseV1Error,
  } = useQuery(ApplicantIdVerificationPreviewContainerIdentityVerificationCaseV1Document, {
    variables: {
      identityVerificationCaseV1Id: caseId,
    },
  });
  const { data: countryData, error: countryError, loading: countryLoading } = useQuery(lookupCountryQuery);

  const [identityVerificationCaseV1Update] = useMutation(ApplicantIdVerificationPreviewContainerIdentityVerificationCaseV1UpdateDocument);
  const [deleteIdCase] = useMutation(ApplicantIdVerificationPreviewContainerIdentityVerificationCaseV1DeleteDocument);

  useProfileSubmissionCheck(applicantUserData, props.applicantUserId as string);

  const handleSave = async () => {
    try {
      const result = await identityVerificationCaseV1Update({
        variables: {
          input: {
            id: identityVerificationCaseV1Data?.identityVerificationCaseV1?.id,
            caseDetails: {
              application: {
                isAttestationComplete: true,
              },
            },
          },
        },
      });

      if (result.data?.identityVerificationCaseV1Update?.status.success) {
        message.success("Attestation saved successfully");
        // insure proper case is in local storage
        setItem('caseId', identityVerificationCaseV1Data?.identityVerificationCaseV1?.id);
        navigate(`${resolvedPath}/process-payment`);
      } else {
        message.error("Failed to save passport details");
      }
    } catch (error) {
      message.error("Failed to save Attestation");
    }
  };

  const handleOnUploadSuccess = async (uploadedData: AzureBlobResponse, fieldName: string) => {
    const assetName = fieldName === "photo" ? "photoAssets" : "passportAssets";
    const { blobUrl, versionId } = uploadedData;
    const input = {
      id: identityVerificationCaseV1Data?.identityVerificationCaseV1?.id,
      caseDetails: {
        application: {
          [assetName]: {
            [`${fieldName}`]: blobUrl,
            [`${fieldName}Version`]: versionId,
          },
        },
      },
    };
    identityVerificationCaseV1Update({
      variables: {
        input,
      },
    }).then((response) => {
      if (response.data?.identityVerificationCaseV1Update.status.success) {
        message.success("File uploaded successfully");
      } else {
        message.error("Error saving Application data");
      }
    });
  };

  const handleWithdrawRequest = async () => {
    deleteIdCase({
      variables: {
        input: {
          caseId: identityVerificationCaseV1Data?.identityVerificationCaseV1?.id,
        }
      },
    }).then((response) => {
      if (response.data?.identityVerificationCaseV1Delete.status.success) {
        message.success("Case withdrawn successfully");
        navigate('/applicant/onboarding')
      } else {
        message.error("Failed to withdraw case");
      }
    })
  };

  if (applicantUserData?.applicantUser) {
    return (
      <ComponentQueryLoader
        loading={applicantUserLoading || identityVerificationCaseV1Loading || countryLoading}
        error={applicantUserError || identityVerificationCaseV1Error || countryError}
        hasData={
          applicantUserData?.applicantUser &&
          identityVerificationCaseV1Data?.identityVerificationCaseV1 &&
          countryData?.countries?.eventPayload?.countries
        }
        hasDataComponent={
          <IdVerificationPreview
            applicantUserData={applicantUserData.applicantUser as ApplicantUser}
            caseDetailApplicationData={
              identityVerificationCaseV1Data?.identityVerificationCaseV1?.caseDetails?.application as IdentityVerificationCaseV1CaseDetailsApplication
            }
            countryData={countryData?.countries?.eventPayload?.countries as Country[]}
            caseId={identityVerificationCaseV1Data?.identityVerificationCaseV1?.id as string}
            onSave={handleSave}
            onWithdrawRequest={handleWithdrawRequest}
            onUploadSuccess={handleOnUploadSuccess}
          />
        }
        errorComponent={
          <>
            {applicantUserError && <Alert message="Error loading applicant data" type="error" />}
            {identityVerificationCaseV1Error && <Alert message="Error loading case data" type="error" />}
            {countryError && (
              <Alert message={`There was an error loading the country data. Please try again! ${countryError.message}`} type="error" />
            )}
          </>
        }
      />
    );
  }
};
